import { log, sleep } from '@ms-ows/logging'

import {
  addElementClickHandler,
  addResizeHandler,
  checkIntersection,
  createDivElement,
  getOrCreateSubElement,
  getTargetElementInfo,
  watchForElementHidden,
} from '../../dom'
import { loadFabricDependencies } from '../../fabric-dependencies'
import {
  ActionType,
  HostRenderSurfaceParams,
  SurfaceParams,
  TargetElementInfo,
} from '../../models'
import { getOfficeWebSurfaceContainer } from '../../surfaces/office-web-surface-container'
import { isValue } from '../../utils'
import { performBeaconCallback } from '../beacon'
import {
  getRenderAppElementId,
  SURFACE_DATA_ATTRIBUTE_NAME,
  SurfaceLaunchType,
} from '../helpers'
import { initializeFabricIcons } from '../preload'
import { removeSurface } from '../remove'
import {
  getLaunchTime,
  getPageContext,
  getRethrowExceptions,
  getSurfaceContainerId,
  pushCleanup,
  setSurfaceContainerId,
} from '../state'

import { renderHostSingle } from './host'

const getRenderAppLayerElementId = () => `${getSurfaceContainerId()}-Layer`

const addTargetElementClickHandler = ({
  isAlternateTrigger = false,
  surfaceParams,
  targetElementInfo: { selector, targetElement },
  onClick,
}: {
  isAlternateTrigger?: boolean
  surfaceParams?: SurfaceParams
  targetElementInfo: TargetElementInfo
  onClick(): void
}) => {
  const time = Date.now()

  return addElementClickHandler(targetElement, () => {
    log.userActionEvent(0x23314822 /* tag_9mu68 */, 'TargetElementClicked', {
      customData: {
        isAlternateTrigger,
        selector,
        timeToClick: Date.now() - time,
      },
    })

    // user understood the message and performed the click action, we 'converted' the user.
    performBeaconCallback('conversion', surfaceParams as SurfaceParams)

    const clickAction = surfaceParams && surfaceParams.targetAction
    if (clickAction && typeof clickAction === 'function') {
      clickAction()
    }

    onClick()
  })
}

const addHidingElementClickHandler = ({
  surfaceParams,
  targetElementInfo: { selector, targetElement },
  onClick,
}: {
  surfaceParams?: SurfaceParams
  targetElementInfo: TargetElementInfo
  onClick(): void
}) => {
  const time = Date.now()

  return addElementClickHandler(targetElement, () => {
    log.userActionEvent(0x23314823 /* tag_9mu69 */, 'HidingElementClicked', {
      customData: {
        selector,
        timeToClick: Date.now() - time,
      },
    })

    performBeaconCallback('dismiss', surfaceParams as SurfaceParams)

    onClick()
  })
}

export const renderInternal = async (
  surfaceParams: SurfaceParams,
  { selector, targetElement }: TargetElementInfo,
  surfaceLaunchedEvent?: (eventName?: SurfaceLaunchType) => void,
  onUpdated?: () => void,
  onClosed?: (button: ActionType) => void
) => {
  try {
    // Root container for surface to render in document body
    let renderRootContainer = document.getElementById(getSurfaceContainerId())
    if (!renderRootContainer) {
      renderRootContainer = createDivElement({
        attributes: {
          [SURFACE_DATA_ATTRIBUTE_NAME]: 'true',
          style: 'all: initial',
        },
        id: getSurfaceContainerId(),
      })
    }

    // Sub-element where surface elements render
    const renderAppElement = getOrCreateSubElement(
      getRenderAppElementId(),
      renderRootContainer
    )

    const fabricDependencies = await loadFabricDependencies()
    const { createElement: h, render, setDefaultTarget } = fabricDependencies

    // if this hasn't been done already, it needs to be done now
    initializeFabricIcons(fabricDependencies)

    if (setDefaultTarget) {
      // Sub-element where office-ui-fabric-react callouts layers render
      const renderFabricLayerElement = getOrCreateSubElement(
        getRenderAppLayerElementId(),
        renderRootContainer
      )

      // tell ui set a target div for layers
      setDefaultTarget(`#${renderFabricLayerElement.id}`)
    }

    // Render surface element
    render(
      h(getOfficeWebSurfaceContainer(fabricDependencies), {
        ...getPageContext(),
        ...surfaceParams,
        onClosed,
        onUpdated,
        selector,
        targetElement,
      }),
      renderAppElement,
      surfaceLaunchedEvent
    )
  } catch (err) {
    log.error(
      0x23602880 /* tag_9yc8a */,
      'error thrown in renderInternal',
      selector,
      err
    )

    onClosed && onClosed('error')

    if (getRethrowExceptions()) {
      throw err
    }
  }
}

export const getAndProcessTargetElement = (
  surfaceParams: SurfaceParams,
  surfaceLaunchedEvent?: (eventName?: SurfaceLaunchType) => void,
  onClosed?: (button: ActionType) => void
): TargetElementInfo | undefined => {
  const targetElementInfo = getTargetElementInfo(
    surfaceParams.anchorElementSelectors
  )

  if (targetElementInfo) {
    const { selector, targetElement } = targetElementInfo
    const { bottom, left, right, top } =
      (targetElement && targetElement.getBoundingClientRect()) ||
      ({} as DOMRect)

    log.event(0x23602881 /* tag_9yc8b */, 'SurfaceSelectorMatched', {
      customData: {
        anchorHint: surfaceParams.anchorHint,
        selector,

        // location
        bottom,
        left,
        right,
        top,
      },
    })
  } else {
    log.error(
      0x23602882 /* tag_9yc8c */,
      "Couldn't find element",
      surfaceParams.anchorElementSelectors
    )
  }

  // log AA trigger first
  let surfaceLaunchType
  let actionType

  if (surfaceParams.isAA) {
    surfaceLaunchType = 'SurfaceAATriggered'
    actionType = 'isAA'
  } else if (surfaceParams.treatmentType === 0 || surfaceParams.isControl) {
    surfaceLaunchType = 'SurfaceControlTriggered'
    actionType = 'isControl'
  }

  if (surfaceLaunchType !== undefined && actionType !== undefined) {
    if (!surfaceParams.anchorElementSelectors || targetElementInfo) {
      surfaceLaunchedEvent &&
        surfaceLaunchedEvent(surfaceLaunchType as SurfaceLaunchType)
    }

    onClosed && onClosed(actionType as ActionType)

    return
  }

  if (!targetElementInfo) {
    onClosed && onClosed('error')

    return
  }

  return targetElementInfo
}

const addHandlersForSelectors = (
  selectors: string[] | undefined,
  addHandler: (t: TargetElementInfo) => () => void
) =>
  selectors
    ?.map(s => getTargetElementInfo([s]))
    .filter(isValue)
    .forEach(t => pushCleanup(addHandler(t)))

const getTargetElementAndRender =
  (
    surfaceParams: SurfaceParams,
    removeOnTargetClick = true,
    renderCallback:
      | ((surfaceParams: HostRenderSurfaceParams[]) => Promise<(() => void)[]>)
      | undefined,
    surfaceLaunchedEvent: ((eventName?: SurfaceLaunchType) => void) | undefined,
    onUpdated: (() => void) | undefined,
    onClosed: ((button: ActionType) => void) | undefined
  ) =>
  async () => {
    const targetElementInfo = getAndProcessTargetElement(
      surfaceParams,
      surfaceLaunchedEvent,
      onClosed
    )

    if (!targetElementInfo) {
      return
    }

    if (
      surfaceParams.targetHidingElementSelectors
        ?.map(s => getTargetElementInfo([s]))
        .filter(isValue)
        .some(e =>
          checkIntersection(targetElementInfo.targetElement, e.targetElement)
        )
    ) {
      log.warn(0x23314840 /* tag_9mu7a */, 'Target covered', {
        selector: targetElementInfo.selector,
      })
      performBeaconCallback('dismiss', surfaceParams)

      return
    }

    const onClick = () => {
      if (removeOnTargetClick) {
        removeSurface()
      }
    }

    pushCleanup(
      addTargetElementClickHandler({
        onClick,
        surfaceParams,
        targetElementInfo,
      })
    )

    addHandlersForSelectors(surfaceParams.alternativeTriggerSelectors, t =>
      addTargetElementClickHandler({
        isAlternateTrigger: true,
        onClick,
        surfaceParams,
        targetElementInfo: t,
      })
    )

    addHandlersForSelectors(surfaceParams.targetHidingClickSelectors, t =>
      addHidingElementClickHandler({
        onClick,
        surfaceParams,
        targetElementInfo: t,
      })
    )

    pushCleanup(
      watchForElementHidden(targetElementInfo.targetElement, () => {
        log.warn(0x23314841 /* tag_9mu7b */, 'Target removed', {
          selector: targetElementInfo.selector,
        })
        performBeaconCallback('dismiss', surfaceParams)
        removeSurface()
      })
    )

    pushCleanup(
      addResizeHandler(() => {
        log.warn(0x23314842 /* tag_9mu7c */, 'Page resized', {
          selector: targetElementInfo.selector,
        })
        performBeaconCallback('dismiss', surfaceParams)
        removeSurface()
      })
    )

    if (!surfaceParams.useNativeRender && renderCallback) {
      await renderHostSingle(
        onClosed,
        onUpdated,
        renderCallback,
        surfaceLaunchedEvent,
        surfaceParams,
        targetElementInfo,
        getLaunchTime(),
        getPageContext().surfaceId
      )
    } else {
      await renderInternal(
        surfaceParams,
        targetElementInfo,
        surfaceLaunchedEvent,
        onUpdated,
        onClosed
      )
    }
  }

export const renderSingle = async (
  surfaceParams: SurfaceParams,
  renderTargetId: string,
  removeOnTargetClick = true,
  renderCallback:
    | ((surfaceParams: HostRenderSurfaceParams[]) => Promise<(() => void)[]>)
    | undefined,
  surfaceLaunchedEvent?: (eventName?: SurfaceLaunchType) => void,
  onUpdated?: () => void,
  onClosed?: (button: ActionType) => void
) => {
  setSurfaceContainerId(renderTargetId)

  const _getTargetElementAndRender = getTargetElementAndRender(
    surfaceParams,
    removeOnTargetClick,
    renderCallback,
    surfaceLaunchedEvent,
    onUpdated,
    onClosed
  )

  // TODO: add retry logic if anchor elements are not yet ready
  if (surfaceParams.delayLoad !== undefined && surfaceParams.delayLoad > 0) {
    sleep(surfaceParams.delayLoad)
      .then(_getTargetElementAndRender)
      .catch((error: unknown) => {
        log.error(
          0x23602883 /* tag_9yc8d */,
          'failed to render delay loaded surface',
          error
        )

        onClosed && onClosed('error')
      })
  } else {
    await _getTargetElementAndRender()
  }
}
